import moment from 'moment';
import {
    ACTUAL_COHORT_PERIOD_CELL,
    ACTUAL_COHORT_PERIOD_NAME,
    ADN_ACCOUNT_ID_FIELD,
    COHORT_PERIOD_FIELD,
    COLLAPSED_FIELDS,
    COLUMNS,
    DICT_FIELDS,
    EVENT_FIELD,
    PER_ROW_DICTS,
    VALUE_CURRENCY_FIELD,
    CSV_DATE_HEADER,
    CSV_VALUE_HEADER,
    CSV_COLUMN_EXAMPLES,
} from './consts';

// Client-only unique key so React can track rows (including brand-new, id-less rows) across edits.
let rowKeyCounter = 0;
export const nextRowKey = () => {
    rowKeyCounter += 1;
    return `row_${rowKeyCounter}`;
};

export const SKIP_VALUE = 'SKIP';
export const SKIP_DISPLAY = 'Skip';
export const isSkipToken = value => typeof value === 'string' && value.trim().toUpperCase() === SKIP_VALUE;

// Normalize a server row (from get_table) into the flat shape the grid edits in place.
// The backend nests the date under `start_date: {startDate, endDate}`; everything else is
// already a `{name, display_name}` dict object (or null).
export const normalizeRow = serverRow => {
    const row = {
        _key: nextRowKey(),
        id: serverRow.id ?? null,
        startDate: serverRow.start_date ? serverRow.start_date.startDate || null : null,
        value: serverRow.special_value === SKIP_VALUE ? SKIP_DISPLAY : `${serverRow.value ?? ''}`,
    };

    DICT_FIELDS.forEach(field => {
        row[field] = serverRow[field] || null;
    });

    return row;
};

export const collapseRows = rows => {
    const bySignature = new Map();
    const result = [];
    rows.forEach(row => {
        const country = row.country || null;
        if (!country) {
            result.push({ ...row, country: [], _countryIds: {} });
            return;
        }
        const key = nonCountrySignature(row);
        let logical = bySignature.get(key);
        if (!logical) {
            logical = { ...row, id: null, country: [], _countryIds: {} };
            bySignature.set(key, logical);
            result.push(logical);
        }
        if (!(country.name in logical._countryIds)) {
            logical.country.push(country);
        }
        logical._countryIds[country.name] = row.id ?? null;
    });
    return result;
};

export const explodeRow = row => {
    const { _countryIds = {}, country, ...rest } = row;
    const countries = country && country.length ? country : [null];
    return countries.map(single => ({
        ...rest,
        country: single,
        id: single ? _countryIds[single.name] ?? null : row.id ?? null,
    }));
};

export const createEmptyRow = (isValueCurrencyEnabled = false) => {
    const row = {
        _key: nextRowKey(),
        id: null,
        startDate: null,
        value: '',
    };

    DICT_FIELDS.forEach(field => {
        row[field] = COLLAPSED_FIELDS.includes(field) ? [] : null;
    });

    // Explicit rather than implied: a blank cell used to save as Actual silently.
    row[COHORT_PERIOD_FIELD] = { ...ACTUAL_COHORT_PERIOD_CELL };

    if (!isValueCurrencyEnabled) {
        delete row[VALUE_CURRENCY_FIELD];
    }

    return row;
};

// The visible dictionary columns for a table, in display order, from its `visibility_cols`.
export const getVisibleColumns = (visibilityCols = []) => {
    if (!visibilityCols || !visibilityCols.length) {
        return COLUMNS;
    }
    return COLUMNS.filter(column => visibilityCols.includes(column.field));
};

// Cache key and request scope for one row's per-row dictionary. `null` = not fetchable yet.
export const getRowScopeKey = (field, row) => {
    const scope = PER_ROW_DICTS[field];
    if (!scope) {
        return null;
    }
    const values = scope.parents.map(parent => (row[parent] ? row[parent].name : ''));
    if (scope.requiresScope && values.some(value => !value)) {
        return null;
    }
    return values.join('|');
};

// The per-row columns a change to `field` invalidates — the inverse of `parents`.
export const getDependentFields = field =>
    Object.keys(PER_ROW_DICTS).filter(dependent => PER_ROW_DICTS[dependent].parents.includes(field));

// The dictionary backing one row's cell: a shared array, or a keyed map for the per-row columns.
// `undefined` (not fetched yet) is distinct from empty: isDictCellValid passes it as "cannot
// validate", so an unopened cell never reds out.
export const getRowDictionary = (dictionaries, field, row) => {
    if (!PER_ROW_DICTS[field]) {
        return dictionaries[field];
    }
    const key = getRowScopeKey(field, row);
    return key === null ? undefined : (dictionaries[field] || {})[key];
};

export const NON_COUNTRY_DICT_FIELDS = DICT_FIELDS.filter(field => !COLLAPSED_FIELDS.includes(field));

const nonCountrySignature = row =>
    JSON.stringify({
        date: row.startDate || null,
        value: parseValue(row.value),
        fields: NON_COUNTRY_DICT_FIELDS.reduce((acc, field) => {
            acc[field] = row[field] ? row[field].name : null;
            return acc;
        }, {}),
    });

// Empty string / null → null; anything else → Number. Mirrors the legacy "value === 0 || value" test.
export const parseValue = value => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
};

const logicalSignature = row =>
    JSON.stringify({
        date: row.startDate || null,
        value: parseValue(row.value),
        fields: NON_COUNTRY_DICT_FIELDS.reduce((acc, field) => {
            acc[field] = row[field] ? row[field].name : null;
            return acc;
        }, {}),
        countries: (row.country || []).map(c => c.name).sort(),
    });

export const buildOriginalSignatures = rows => new Set(rows.map(logicalSignature));

// The condition fields that make two rows "the same rule" for the one-cohort-period rule: every
// dictionary condition except cohort_period (the axis we constrain) and value_currency (an attribute
// of the value, not a targeting condition). Country is matched separately, per exploded row.
const COHORT_CONFLICT_FIELDS = NON_COUNTRY_DICT_FIELDS.filter(
    field => field !== COHORT_PERIOD_FIELD && field !== VALUE_CURRENCY_FIELD
);

// Identity of a single-country (exploded) row's targeting, ignoring cohort_period and value. Rows
// sharing this key may hold at most one cohort period between them: the cost calc matches per country
// and cannot sum overlapping periods, so two periods for the same targeting is ambiguous. Exploded
// (one country per key) so partially-overlapping country sets still collide on the shared country.
// adn_account_id's `name` is a slugified account_id ('AcmeAds' and 'acmeads' both collapse to
// 'acmeads'), while the server groups by Account pk. display_name is the raw account_id, unique per
// org, so it matches the server's grouping; every other field's `name` is already a stable canonical id.
const cohortConflictFieldValue = (row, field) => {
    const cell = row[field];
    if (!cell) {
        return null;
    }
    return field === ADN_ACCOUNT_ID_FIELD ? cell.display_name || cell.name : cell.name;
};

const cohortConflictKey = row =>
    JSON.stringify({
        date: row.startDate || null,
        country: row.country ? row.country.name : null,
        fields: COHORT_CONFLICT_FIELDS.reduce((acc, field) => {
            acc[field] = cohortConflictFieldValue(row, field);
            return acc;
        }, {}),
    });

// A cell's cohort period as a comparable key. An unset cell (new row) means Actual, the same as the
// explicit Actual option, so both collapse to one value - otherwise "unset vs Actual" would read as
// two periods and wrongly flag an exact duplicate as a conflict.
const cohortPeriodKey = row => {
    const cell = row[COHORT_PERIOD_FIELD];
    return cell ? cell.name : ACTUAL_COHORT_PERIOD_NAME;
};

// A dict cell is valid when it resolves to an entry in its dictionary (matched by `name`).
// An empty dictionary is treated as "cannot validate" and left alone, to avoid false errors
// for restricted users who only receive a subset of dictionary values.
const isDictCellValid = (cell, dictionary) => {
    if (!cell) {
        return true;
    }
    if (!dictionary || !dictionary.length) {
        return true;
    }
    return dictionary.some(entry => entry.name === cell.name);
};

const isValidDate = value => {
    if (!value) {
        return true;
    }
    return moment(value, 'YYYY-MM-DD', true).isValid();
};

// Build the update payload from the current grid rows.
//
// Parity notes with the legacy Angular controller:
//  - Every row that has a value is sent (the backend deletes any existing row absent from the
//    payload), not just edited rows.
//  - Unedited rows skip cell validation so a restricted user's untouched rows never block a save.
//  - The lone blank starter row (single row, no conditions, no date) is ignored, not flagged.
//
// Returns { payload, errorsByKey, valid, hasInvalidDate, hasInvalidValue }.
export const extractTableData = ({
    rows,
    originalSignatures,
    dictionaries,
    isValueCurrencyEnabled,
    isEventRequired,
}) => {
    const payload = [];
    const errorsByKey = {};
    let valid = true;
    let hasInvalidDate = false;
    let hasInvalidValue = false;
    let hasCohortConflict = false;

    // One entry per saved (exploded) row, used after the loop to reject rows that repeat the same
    // targeting with a different cohort period.
    const cohortConflictEntries = [];

    const totalRows = rows.length;

    rows.forEach(row => {
        const errors = {};
        let hasConditions = false;

        const unedited = originalSignatures.has(logicalSignature(row));

        if (!unedited) {
            DICT_FIELDS.forEach(field => {
                if (!isValueCurrencyEnabled && field === VALUE_CURRENCY_FIELD) {
                    return;
                }
                if (COLLAPSED_FIELDS.includes(field)) {
                    (row.country || []).forEach(cell => {
                        if (isDictCellValid(cell, getRowDictionary(dictionaries, field, row))) {
                            hasConditions = true;
                        } else {
                            errors.country = true;
                        }
                    });
                    return;
                }
                if (row[field]) {
                    if (!isDictCellValid(row[field], getRowDictionary(dictionaries, field, row))) {
                        errors[field] = true;
                    } else if (field !== COHORT_PERIOD_FIELD || row[field].name !== ACTUAL_COHORT_PERIOD_NAME) {
                        // Only the *default* window is non-conditional. Every new row now carries Actual, so
                        // counting that would make the blank starter row look configured and drop its
                        // exemption from the value and event checks below - but a window the user actually
                        // chose is a deliberate edit, and the row should be validated like any other.
                        hasConditions = true;
                    }
                }
            });
        }

        // A value counts as content: the exemption exists so an untouched blank row is not flagged, and a
        // row someone typed a payout into is not untouched. Without this a lone row with a value and no
        // event skipped the event check here and failed server-side as an opaque save error instead.
        const isEmptyStarterRow = totalRows === 1 && !hasConditions && !row.startDate && !row.value;

        const isSkip = isSkipToken(row.value);
        const numericValue = isSkip ? null : parseValue(row.value);

        if (isValueCurrencyEnabled && !isSkip && !row[VALUE_CURRENCY_FIELD] && !isEmptyStarterRow) {
            errors[VALUE_CURRENCY_FIELD] = true;
        }

        // A CPE table prices each row by its own event, so a blank one is not a wildcard.
        if (isEventRequired && !row[EVENT_FIELD] && !isEmptyStarterRow) {
            errors[EVENT_FIELD] = true;
        }

        if (!isValidDate(row.startDate)) {
            hasInvalidDate = true;
        }

        if (isSkip || numericValue !== null) {
            explodeRow(row).forEach(single => {
                const payloadRow = {
                    id: single.id ?? null,
                    date: single.startDate || null,
                    value: isSkip ? null : numericValue,
                    special_value: isSkip ? SKIP_VALUE : null,
                };
                DICT_FIELDS.forEach(field => {
                    payloadRow[field] = single[field] || null;
                });
                payload.push(payloadRow);
                // A skip row prices nothing, so it cannot create a cohort-period ambiguity - two
                // all-skip rows differing only by window are not a conflict. The server backstop
                // excludes skip rows the same way.
                if (!isSkip) {
                    cohortConflictEntries.push({
                        rowKey: row._key,
                        key: cohortConflictKey(single),
                        cohort: cohortPeriodKey(single),
                    });
                }
            });
        } else if (!isEmptyStarterRow) {
            errors.value = true;
            hasInvalidValue = true;
        }

        if (Object.keys(errors).length) {
            errorsByKey[row._key] = errors;
            valid = false;
        }
    });

    // A given targeting may be priced by only one cohort period. Group the saved rows by targeting
    // (cohort_period and value excluded) and flag every row of any group that holds more than one.
    //
    // Only where a CPE sum rule reads the window - isEventRequired carries that condition. On any other
    // table the evaluator ignores cohort_period entirely, so two rows differing only by it change nothing
    // and blocking the save would refuse a configuration that has no effect either way. The CSV import
    // applies the same condition, so the two paths accept and reject the same files.
    const cohortGroups = new Map();
    (isEventRequired ? cohortConflictEntries : []).forEach(entry => {
        let group = cohortGroups.get(entry.key);
        if (!group) {
            group = { rowKeys: new Set(), cohorts: new Set() };
            cohortGroups.set(entry.key, group);
        }
        group.rowKeys.add(entry.rowKey);
        group.cohorts.add(entry.cohort);
    });
    cohortGroups.forEach(group => {
        if (group.cohorts.size < 2) {
            return;
        }
        hasCohortConflict = true;
        valid = false;
        group.rowKeys.forEach(rowKey => {
            errorsByKey[rowKey] = { ...errorsByKey[rowKey], [COHORT_PERIOD_FIELD]: true };
        });
    });

    return { payload, errorsByKey, valid, hasInvalidDate, hasInvalidValue, hasCohortConflict };
};

// "Always" when no date is set, otherwise the formatted rule start date.
export const formatStartDate = (startDate, alwaysLabel) => {
    if (!startDate) {
        return alwaysLabel;
    }
    return moment(startDate).format('YYYY-MM-DD');
};

const escapeCsvCell = cell => {
    const text = cell === null || cell === undefined ? '' : `${cell}`;
    if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
};

// Serialize the (already filtered) grid rows to CSV, matching the legacy column contract:
// "Rule Start Date", each visible dictionary column (by field key), "value", and optionally
// "value_currency".
export const buildCsv = ({ rows, visibleColumns, isValueCurrencyEnabled }) => {
    const columnKeys = [CSV_DATE_HEADER, ...visibleColumns.map(column => column.field), CSV_VALUE_HEADER];
    if (isValueCurrencyEnabled) {
        columnKeys.push(VALUE_CURRENCY_FIELD);
    }

    // A column may carry the cell's `name` (a stable id) instead of its display name when the display name
    // is not unique - the event label can repeat across an app's slots. Mirrors REF_TABLE_CSV_ID_COLUMNS.
    const csvValueKey = Object.fromEntries(
        visibleColumns.map(column => [column.field, column.csvValue || 'display_name'])
    );

    const headerLine = columnKeys.map(escapeCsvCell).join(',');

    const exploded = rows.flatMap(explodeRow);
    const dataLines = exploded.map(row =>
        columnKeys
            .map(key => {
                if (key === CSV_DATE_HEADER) {
                    return escapeCsvCell(row.startDate || '');
                }
                if (key === CSV_VALUE_HEADER) {
                    return escapeCsvCell(row.value === '' || row.value === null ? '' : row.value);
                }
                return escapeCsvCell(row[key] ? row[key][csvValueKey[key] || 'display_name'] || '' : '');
            })
            .join(',')
    );

    return [headerLine, ...dataLines].join('\n');
};

// `isEventRequired` comes from the server: it depends on the org's metric rules, not just the columns, so
// the grid cannot work it out from visibleColumns alone.
export const buildImportFormatRows = ({ visibleColumns, isValueCurrencyEnabled, isEventRequired = false }) => {
    const formatRow = (column, required) => ({
        column,
        required,
        example: CSV_COLUMN_EXAMPLES[column] || '',
    });

    const eventRequired = isEventRequired;
    const rows = [
        formatRow(CSV_DATE_HEADER, false),
        ...visibleColumns.map(column => formatRow(column.field, column.field === EVENT_FIELD && eventRequired)),
        formatRow(CSV_VALUE_HEADER, true),
    ];
    if (isValueCurrencyEnabled) {
        rows.push(formatRow(VALUE_CURRENCY_FIELD, true));
    }
    return rows;
};

// Serialize the backend's per-row import errors to a downloadable CSV (Row, Column, Reason).
// When the backend capped the returned errors, `totalErrors` exceeds the list length and a
// trailing note row makes the truncation explicit in the downloaded file.
export const buildErrorCsv = (errors = [], totalErrors = null) => {
    const headerLine = 'Row,Column,Reason';
    const dataLines = errors.map(error => [error.row, error.column, error.reason].map(escapeCsvCell).join(','));
    if (totalErrors !== null && totalErrors > errors.length) {
        const note = `... ${totalErrors -
            errors.length} more errors not shown - fix these and re-upload to see the rest`;
        dataLines.push(['', '', note].map(escapeCsvCell).join(','));
    }
    return [headerLine, ...dataLines].join('\n');
};

// Union by `name`, so a per-row dictionary accumulates typeahead results instead of replacing them.
export const mergeDictionaryEntries = (existing, incoming = []) => {
    if (!existing || !existing.length) {
        return incoming;
    }
    const seen = new Set(existing.map(entry => entry.name));
    return [...existing, ...incoming.filter(entry => !seen.has(entry.name))];
};

// Turn a dictionary of `{name, display_name}` entries into react-select options.
export const toOptions = (dictionary = []) =>
    dictionary.map(entry => ({ value: entry.name, label: entry.display_name }));

// Filter the grid rows by the per-column free-text filters (case-insensitive substring on display_name).
export const applyFilters = (rows, filters) => {
    const activeFilters = Object.entries(filters).filter(([, query]) => query && query.length);
    if (!activeFilters.length) {
        return rows;
    }
    return rows.filter(row =>
        activeFilters.every(([field, query]) => {
            const q = query.toLowerCase();
            if (COLLAPSED_FIELDS.includes(field)) {
                return (row.country || []).some(cell => (cell.display_name || '').toLowerCase().includes(q));
            }
            const cell = row[field];
            const display = cell ? cell.display_name || '' : '';
            return display.toLowerCase().includes(q);
        })
    );
};

export const hasActiveFilters = filters => Object.values(filters).some(query => query && query.length);

// Column widths are a per-user convenience, stored per table so each page keeps its own layout.
// Keyed by column field rather than position: columns are added and removed in the admin portal, and a
// positional key would silently transfer one column's width to another the moment the set changes.
const COLUMN_WIDTHS_STORAGE_PREFIX = 'referenceTable.columnWidths.';

const columnWidthsKey = tableName => `${COLUMN_WIDTHS_STORAGE_PREFIX}${tableName}`;

export const readColumnWidths = tableName => {
    if (!tableName) {
        return {};
    }
    try {
        const stored = JSON.parse(window.localStorage.getItem(columnWidthsKey(tableName)) || '{}');
        if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
            return {};
        }
        // Anything that is not a usable width is dropped rather than applied: a hand-edited or
        // half-written entry must not be able to collapse a column.
        return Object.fromEntries(Object.entries(stored).filter(([, width]) => Number.isFinite(width) && width > 0));
    } catch (error) {
        return {};
    }
};

export const writeColumnWidths = (tableName, widths) => {
    if (!tableName) {
        return;
    }
    try {
        window.localStorage.setItem(columnWidthsKey(tableName), JSON.stringify(widths));
    } catch (error) {
        // Storage can be unavailable (private browsing, quota). Widths are cosmetic, so losing them
        // silently is better than breaking the grid.
    }
};
